import React, { useState, useRef } from 'react';
import API from '../api';
import { IcCamera, IcCheck, IcAlertTriangle, IcX, IcScan } from './Icons';

export default function Scan() {
  const [mode, setMode] = useState('entry');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const scan = async () => {
    if (!fileRef.current?.files[0]) return;
    setLoading(true); setResults(null);
    const fd = new FormData();
    fd.append('school_id', 1);
    fd.append('event_type', mode);
    fd.append('photo', fileRef.current.files[0]);
    try {
      const r = await API.post('/recognition/scan', fd);
      setResults(r.data);
    } catch (e) {
      setResults({ error: e.response?.data?.detail || 'Erreur' });
    }
    setLoading(false);
    fileRef.current.value = '';
  };

  const ResultItem = ({ r }) => {
    if (r.identified) {
      const isTeacher = r.person_type === 'teacher';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, background: 'var(--emerald-l)', border: '1px solid #A7F3D0' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--emerald)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IcCheck size={18} stroke="#fff" strokeWidth={2.5} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#064E3B' }}>{r.person.name}</div>
            <div style={{ fontSize: 12, color: '#065F46', marginTop: 2 }}>
              {isTeacher ? r.person.subject : r.person.class} • Confiance {r.confidence}%
            </div>
          </div>
          <span className={`badge ${r.event_type === 'entry' ? 'badge-green' : 'badge-orange'}`} style={{ flexShrink: 0 }}>
            {r.event_type === 'entry' ? 'Entrée' : 'Sortie'}
          </span>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, background: r.is_repeat ? 'var(--amber-l)' : 'var(--red-l)', border: `1px solid ${r.is_repeat ? '#FCD34D' : '#FCA5A5'}` }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: r.is_repeat ? 'var(--amber)' : 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IcAlertTriangle size={18} stroke="#fff" strokeWidth={2} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: r.is_repeat ? '#92400E' : '#991B1B' }}>
            {r.unknown?.code || 'Inconnu'}
          </div>
          <div style={{ fontSize: 12, color: r.is_repeat ? '#B45309' : '#B91C1C', marginTop: 2 }}>{r.message}</div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 className="page-title">Reconnaissance faciale</h1>
        <p className="page-subtitle">Uploadez une photo pour identifier une personne</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Panneau gauche — contrôles */}
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)', marginBottom: 18 }}>Paramètres</div>

          <div style={{ marginBottom: 18 }}>
            <label className="label">Type d'événement</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              {['entry', 'exit'].map(t => (
                <button key={t} onClick={() => setMode(t)} style={{
                  flex: 1, padding: '9px 14px', border: `2px solid ${mode === t ? (t === 'entry' ? 'var(--emerald)' : 'var(--amber)') : 'var(--slate-200)'}`,
                  borderRadius: 8, background: mode === t ? (t === 'entry' ? 'var(--emerald-l)' : 'var(--amber-l)') : '#fff',
                  color: mode === t ? (t === 'entry' ? '#065F46' : '#92400E') : 'var(--slate-500)',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                }}>
                  {t === 'entry' ? 'Entrée' : 'Sortie'}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="label">Photo à analyser</label>
            <div style={{ marginTop: 4, border: '2px dashed var(--slate-200)', borderRadius: 10, padding: '20px', textAlign: 'center', background: 'var(--slate-50)' }}>
              <IcCamera size={28} stroke="var(--slate-300)" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 12, color: 'var(--slate-400)', marginBottom: 10 }}>Déposez une photo ou cliquez</div>
              <input ref={fileRef} type="file" accept="image/*" id="scan-file" style={{ display: 'none' }} />
              <label htmlFor="scan-file" className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                Sélectionner une photo
              </label>
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '11px' }} onClick={scan} disabled={loading}>
            <IcScan size={16} strokeWidth={2} />
            {loading ? 'Analyse en cours...' : 'Lancer la reconnaissance'}
          </button>
        </div>

        {/* Panneau droit — résultat */}
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)', marginBottom: 18 }}>Résultat</div>

          {!results && !loading && (
            <div className="empty" style={{ paddingTop: 40 }}>
              <IcCamera size={40} stroke="var(--slate-300)" />
              <p style={{ marginTop: 14 }}>En attente d'une photo à analyser</p>
            </div>
          )}

          {loading && (
            <div className="empty" style={{ paddingTop: 40 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid var(--indigo-l)', borderTop: '3px solid var(--indigo)', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p>Analyse faciale en cours...</p>
            </div>
          )}

          {results && results.error && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 16, background: 'var(--red-l)', borderRadius: 10, border: '1px solid #FCA5A5' }}>
              <IcX size={18} stroke="var(--red)" strokeWidth={2} />
              <span style={{ fontSize: 13, color: '#991B1B', fontWeight: 500 }}>{results.error}</span>
            </div>
          )}

          {results && results.results && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--slate-400)', marginBottom: 14 }}>
                {results.count} visage{results.count > 1 ? 's' : ''} détecté{results.count > 1 ? 's' : ''}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {results.results.map((r, i) => <ResultItem key={i} r={r} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
